import React from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { LeaderboardContestant, PositionCell } from './ContestLeaderboard';
import { Countdown, CountdownDisplayType } from '../Countdown/Countdown';
import { HealthIcon, TimerIcon } from 'components/Icons/Icons';
import { PlatformButton } from 'components/Button/PlatformButton';
import { routes } from 'routing/routes';

export enum LeaderboardGameType {
  // eslint-disable-next-line no-unused-vars
  BeastBattler = 'beast-battler',
  // eslint-disable-next-line no-unused-vars
  TwilightShift = 'twilight-shift',
}

export const DEFAULT_LEADERBOARD_GAME_TYPE = LeaderboardGameType.TwilightShift;

export const isValidLeaderboardGameType = (gameType: string): gameType is LeaderboardGameType =>
  Object.values(LeaderboardGameType).includes(gameType as LeaderboardGameType);

export type ContestConfig = {
  icon: JSX.Element;
  cta: JSX.Element;
};

const gameTypeToButtonText = (gameType: LeaderboardGameType) => {
  switch (gameType) {
    case LeaderboardGameType.BeastBattler:
      return 'Beast Battler';
    case LeaderboardGameType.TwilightShift:
      return 'Twilight Shift';
  }
};

export const getContestConfig = (gameType: LeaderboardGameType): ContestConfig => {
  const text = `Play ${gameTypeToButtonText(gameType)}`;
  switch (gameType) {
    case LeaderboardGameType.BeastBattler:
      return {
        icon: <HealthIcon />,
        cta: (
          <PlatformButton
            disabled={window.innerWidth <= 768}
            platform="macos"
            text={text}
            link={routes.game()}
          />
        ),
      };
    case LeaderboardGameType.TwilightShift:
    default:
      return {
        icon: <TimerIcon />,
        cta: (
          <PlatformButton
            disabled={window.innerWidth <= 768}
            platform="windows"
            text={text}
            link={routes.launcher()}
          />
        ),
      };
  }
};

export type HeaderMetadata = {
  tooltip: string;
};

export function leaderboardColumnDefinitions(
  type: LeaderboardGameType,
  options: { displayMedal: boolean },
): Array<ColumnDef<LeaderboardContestant<any>, any>> {
  const columnHelper = createColumnHelper<LeaderboardContestant>();

  const positionColumn = columnHelper.accessor((row) => row.position, {
    id: 'position',
    header: '#',
    cell(ctx) {
      return <PositionCell index={ctx.getValue()} displayMedal={options.displayMedal} />;
    },
  });
  const breakerIdColumn = columnHelper.accessor((row) => row.breakerId, {
    id: 'id',
    header: 'Breaker Id',
  });
  switch (type) {
    case LeaderboardGameType.BeastBattler: {
      const beast = createColumnHelper<LeaderboardContestant<LeaderboardGameType.BeastBattler>>();

      return [
        positionColumn,
        breakerIdColumn,
        beast.accessor((row) => row.additionalData.healthRemaining, {
          id: 'healthRemaining',
          header: 'Health Remaining',
          cell(props) {
            return <div>{props.getValue()}</div>;
          },
          meta: {
            tooltip: 'In the event of a tie, the faster run time will be used as a tiebreaker.',
          },
        }),
      ];
    }
    case LeaderboardGameType.TwilightShift: {
      const ts = createColumnHelper<LeaderboardContestant<LeaderboardGameType.TwilightShift>>();
      const timeColumn = ts.accessor((row) => row.additionalData.time, {
        id: 'time',
        header: 'In-Game Time',
        cell: (props) => {
          return (
            <Countdown
              countdown={props.getValue()}
              displayType={CountdownDisplayType.Thin}
              concise
              displayMilliseconds
            />
          );
        },
      });
      return [positionColumn, breakerIdColumn, timeColumn];
    }
  }
}
